import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/i18n/LanguageContext";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t, supportedLanguages } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { path: "/", label: t("nav.home") },
    { path: "/services", label: t("nav.services") },
    { path: "/projets", label: t("nav.projects") },
    { path: "/contact", label: t("nav.contact") },
  ];

  const isActive = (path) => location.pathname === path;

  const currentLang = supportedLanguages.find(l => l.code === language);

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0A0A0A]/95 backdrop-blur-xl shadow-2xl py-3"
          : "bg-[#0A0A0A]/80 backdrop-blur-sm py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center group"
          >
            <img 
              src="/images/logo.png" 
              alt="Perfect Team Renov" 
              className="h-12 sm:h-14 w-auto object-contain transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                className={`relative text-sm uppercase tracking-widest font-medium transition-colors ${
                  isActive(link.path) 
                    ? "text-[#B87333]" 
                    : "text-white/80 hover:text-white"
                }`}
              >
                {link.label}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-[#B87333] to-[#CD7F32]" />
                )}
              </Link>
            ))}
          </div>

          {/* Right side: Language + Phone + CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="language-switcher"
                  className="flex items-center space-x-2 text-white/80 hover:text-white transition-colors px-3 py-2 rounded-full border border-white/20 hover:border-[#B87333]/50"
                >
                  <Globe size={16} />
                  <span className="text-sm font-medium">{currentLang?.code.toUpperCase()}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#1A1A1A] border-[#333]">
                {supportedLanguages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`cursor-pointer ${
                      language === lang.code ? "text-[#B87333]" : "text-white"
                    } hover:bg-[#242424]`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Phone - Always visible and prominent */}
            <a
              href="tel:+32488467522"
              data-testid="phone-cta"
              className="flex items-center space-x-2 px-4 py-2 bg-[#B87333]/20 hover:bg-[#B87333]/30 rounded-full transition-all"
            >
              <Phone size={16} className="text-[#B87333]" />
              <span className="text-white font-medium">+32 488 46 75 22</span>
            </a>
            
            <Link to="/devis">
              <Button
                data-testid="cta-quote-btn"
                className="bg-gradient-to-r from-[#B87333] to-[#CD7F32] hover:from-[#CD8B4A] hover:to-[#D4AF37] text-white px-6 rounded-full shadow-lg hover:shadow-[#B87333]/30 transition-all"
              >
                {t("nav.quote")}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center space-x-2 lg:hidden">
            {/* Mobile Phone - Always visible */}
            <a
              href="tel:+32488467522"
              className="flex items-center justify-center w-10 h-10 bg-[#B87333]/20 rounded-full"
            >
              <Phone size={18} className="text-[#B87333]" />
            </a>
            
            {/* Mobile Language */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-white p-2">
                  <Globe size={20} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#1A1A1A] border-[#333]">
                {supportedLanguages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`cursor-pointer ${
                      language === lang.code ? "text-[#B87333]" : "text-white"
                    }`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              data-testid="mobile-menu-btn"
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-white"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden absolute top-full left-0 right-0 bg-[#0A0A0A] shadow-2xl animate-slide-down border-t border-white/10"
          >
            <div className="px-4 py-6 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.path.replace("/", "") || "home"}`}
                  onClick={() => setIsOpen(false)}
                  className={`block py-4 px-4 text-lg font-medium rounded-xl transition-colors ${
                    isActive(link.path) 
                      ? "text-[#B87333] bg-[#B87333]/10" 
                      : "text-white hover:bg-white/5"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="tel:+32488467522"
                data-testid="mobile-phone-link"
                className="flex items-center space-x-2 py-4 px-4 text-white hover:bg-white/5 rounded-xl"
              >
                <Phone size={20} className="text-[#B87333]" />
                <span>+32 488 46 75 22</span>
              </a>
              <Link to="/devis" onClick={() => setIsOpen(false)}>
                <Button 
                  data-testid="mobile-quote-btn"
                  className="w-full bg-gradient-to-r from-[#B87333] to-[#CD7F32] text-white mt-4 py-6 rounded-full"
                >
                  {t("nav.quote")}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
